import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { PageBackground } from '@/components/ui/page-background';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/seo';
import { ArrowRight, Clock, Mail, MessageSquare } from 'lucide-react';

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');

  const nextSteps = [
    {
      icon: Mail,
      title: "Check your inbox",
      description: "You'll receive a confirmation email from post@affinityaim.com within a few minutes.",
      timing: "Now"
    },
    {
      icon: Clock,
      title: "We review your project",
      description: "Our team will carefully go through your requirements and prepare a detailed response.",
      timing: "Within hours"
    },
    {
      icon: MessageSquare,
      title: "We get back to you",
      description: "Expect a detailed reply with next steps, questions, or a proposal - depending on your project.",
      timing: "Within 24h"
    }
  ];

  return (
    <PageBackground variant="aurora">
      <SEO
        title="Message Received | AffinityAim"
        description="Thanks for reaching out. We'll be in touch within 24 hours."
        canonical="https://www.affinityaim.com/thank-you"
        noindex={true}
      />
      <Navigation />

      <main className="min-h-[80vh] flex items-center justify-center pt-24 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            
            {/* Success indicator */}
            <div className="text-center mb-12 animate-fade-in">
              <div className="relative inline-flex items-center justify-center mb-8">
                <div className="absolute w-24 h-24 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
                <div className="relative w-20 h-20 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-primary" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" className="animate-[draw_0.5s_ease-out_0.3s_forwards]" style={{ strokeDasharray: 24, strokeDashoffset: 24 }} />
                  </svg>
                </div>
              </div>

              <h1 
                className="font-display font-bold text-foreground mb-4"
                style={{ fontSize: 'clamp(32px, 6vw, 48px)', lineHeight: '1.1' }}
              >
                We've received your message
              </h1>
              
              <p className="text-muted-foreground text-lg max-w-md mx-auto">
                Thanks for reaching out{plan ? ` about ${plan}` : ''}. Here's what happens next.
              </p>
            </div>

            {/* Next steps */}
            <div className="space-y-4 mb-12">
              {nextSteps.map((step, index) => (
                <div 
                  key={step.title}
                  className="bg-surface/50 border border-border rounded-xl p-6 flex items-start gap-5 animate-fade-in hover:border-primary/20 transition-colors duration-300"
                  style={{ animationDelay: `${(index + 1) * 150}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <h3 className="font-semibold text-foreground">{step.title}</h3>
                      <span className="text-xs text-primary font-medium whitespace-nowrap">{step.timing}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div 
              className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
              style={{ animationDelay: '600ms' }}
            >
              <Link to="/portfolio">
                <Button variant="default" size="lg" className="group w-full sm:w-auto">
                  View our work
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground w-full sm:w-auto">
                  Back to homepage
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </main>

      <Footer />

      {/* Custom animation for checkmark */}
      <style>{`
        @keyframes draw {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </PageBackground>
  );
};

export default ThankYou;